import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaInfoCircle, FaStar, FaChartLine, FaRobot } from 'react-icons/fa';
import SkeletonCard from "./SkeletonCard";
import PlatformIcon from "../../../components/Common/PlatformIcon";

const AppHeader = ({ trackedApp }) => {
  const navigate = useNavigate();

  const handleGoToDashboard = (tabIndex = 0) => {
    if (trackedApp?.item_id) {
      navigate(`/xr/app/${(trackedApp.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}?id=${trackedApp.item_id}`, {
        state: { selected_tab: tabIndex }
      });
    }
  };

  return (
    <div className="app-header">
      {/* TOP LEFT: Go Back Button */}
      <div className="top-left-actions">
        <Link to="/keyword-tracking" className="go-back-btn-top">
          &larr; Go Back
        </Link>
      </div>

      <div className="app-header-content">
        {/* LEFT SIDE: App Image + Info */}
        <div className="app-header-left">
          <div className="app-image-wrapper">
            {!trackedApp ? (
              <SkeletonCard />
            ) : (
              <>
                <img
                  src={trackedApp.image_url}
                  alt={trackedApp?.name || "Game"}
                  className="app-image"
                />
                {trackedApp?.platform && (
                  <div className="platform-badge">
                    <PlatformIcon platform={trackedApp.platform} />
                  </div>
                )}
              </>
            )}
          </div>

          {trackedApp && (
            <div className="app-info">
              <h2 className="app-name">{trackedApp.name}</h2>

              <div className="app-details">
                <span className="publisher-label">
                  Publisher:&nbsp;
                  {trackedApp.publisher ? (
                    <Link
                      to={`/publisher-apps/${trackedApp.item_id}`}
                      className="publisher-button"
                    >
                      {trackedApp.publisher}
                    </Link>
                  ) : (
                    <span className="publisher-disabled">N/A</span>
                  )}
                </span>

                <span className="category-label">
                  Category: {trackedApp.category || "N/A"}
                </span>
              </div>

              <div className="app-actions">
                <div className="app-actions-left">
                  {trackedApp.platform_url && (
                    <a
                      href={trackedApp.platform_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="visit-store-btn"
                    >
                      <span>Visit Store</span>
                      <PlatformIcon 
                        platform={trackedApp.platform} 
                        className="visit-store-logo"
                      />
                    </a>
                  )}
                  
                  <button
                    onClick={() => handleGoToDashboard()}
                    className="go-to-dashboard-btn"
                    style={{
                      background: "#2A2B37",
                      border: "1px solid #3A3B47",
                      color: "#f43f5e",
                      fontWeight: 600,
                      padding: "6px 12px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#3A3B47"
                      e.currentTarget.style.color = "#fff"
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#2A2B37"
                      e.currentTarget.style.color = "#f43f5e"
                    }}
                  >
                    App Store Listing &rarr;
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* TOP RIGHT: Hub Actions */}
      <div className="top-right-actions">
        <div className="app-actions-right">
          <div className="hub-title-mobile">Check Listing Data</div>
          <div className="hub-actions-inline">
            <span className="hub-title-inline">Check Listing Data:</span>
            {[
              { label: "About", index: 0, icon: FaInfoCircle },
              { label: "Ratings", index: 1, icon: FaStar },
              { label: "Analytics", index: 2, icon: FaChartLine },
              { label: "AI Reports", index: 3, icon: FaRobot },
            ].map((tab) => (
              <button
                key={tab.index}
                onClick={() => handleGoToDashboard(tab.index)}
                className="hub-tile-inline"
                title={`Open ${tab.label}`}
              >
                <tab.icon className="hub-icon" />
                <span className="hub-label">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* STYLES */}
      <style jsx>{`
        .app-header {
          display: flex;
          flex-direction: column;
          padding: 20px;
          background: #1f1f28;
          border-radius: 12px;
          gap: 20px;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
          position: relative;
        }

        .app-header-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 20px;
          flex-wrap: wrap;
        }

        .app-header-left {
          display: flex;
          gap: 20px;
          align-items: center;
          flex: 1;
        }

        .top-right-actions {
          position: absolute;
          top: 20px;
          right: 20px;
        }

        .app-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          margin-top: 10px;
          flex-wrap: wrap;
        }

        .app-actions-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .app-actions-right {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 8px;
        }

        .hub-actions-inline {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .app-image-wrapper {
          position: relative;
          flex-shrink: 0;
        }

        .app-image {
          width: 100px;
          height: 100px;
          border-radius: 12px;
          object-fit: cover;
          border: 1px solid #3a3b47;
        }

        .platform-badge {
          position: absolute;
          bottom: -6px;
          right: -6px;
          background: #1f1f28;
          border: 1px solid #3a3b47;
          border-radius: 50%;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
        }

        .platform-badge img {
          width: 18px;
          height: 18px;
          object-fit: contain;
        }

        .app-info {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .app-name {
          font-size: 1.6rem;
          margin: 0;
          color: #fff;
        }

        .app-details {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          font-size: 0.95rem;
          color: #aaa;
        }

        .publisher-button {
          background: #2a2b37;
          border: 1px solid #3a3b47;
          color: #f43f5e;
          padding: 4px 10px;
          border-radius: 8px;
          text-decoration: none;
          transition: 0.2s;
        }

        .publisher-button:hover {
          background: #3a3b47;
          color: #fff;
        }

        .publisher-disabled {
          color: #9aa0a6;
          font-weight: 600;
        }

        /* FIXED Visit Store button */
        .visit-store-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f43f5e;
          color: #fff;
          padding: 6px 10px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          width: auto;          /* ensures it wraps content */
          min-width: 0;
          white-space: nowrap;  /* prevents stretching */
          transition: 0.2s;
        }

        .visit-store-btn span {
          margin-right: 6px;
        }

        .visit-store-btn img.visit-store-logo {
          height: 18px;
          width: auto;
        }

        .visit-store-btn:hover {
          background: #ff627d;
        }

        .app-header-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }


        .go-back-btn-top {
          display: inline-block;
          background: #2a2b37;
          color: #f43f5e;
          padding: 5px 10px;
          border-radius: 6px;
          font-weight: 600;
          text-decoration: none;
          transition: 0.2s;
          white-space: nowrap;
          border: 1px solid #3a3b47;
          font-size: 0.85rem;
        }

        .go-back-btn-top:hover {
          background: #3a3b47;
          color: #fff;
        }

        .go-back-btn {
          display: inline-block;
          background: #2a2b37;
          color: #f43f5e;
          padding: 4px 8px;
          border-radius: 6px;
          font-weight: 600;
          text-decoration: none;
          transition: 0.2s;
          white-space: nowrap;
          font-size: 0.85rem;
        }

        .go-back-btn:hover {
          background: #3a3b47;
          color: #fff;
        }

        /* Dashboard Hub Widget Styling */
        .dashboard-hub-widget {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: flex-end;
        }

        .hub-title {
          font-size: 1rem;
          font-weight: 600;
          color: #fff;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          margin-bottom: 12px;
        }

        .hub-title-inline {
          font-size: 0.9rem;
          font-weight: 600;
          color: #fff;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          margin-right: 12px;
          white-space: nowrap;
        }

        .hub-title-mobile {
          display: none;
          font-size: 0.8rem;
          font-weight: 600;
          color: #fff;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          margin-bottom: 6px;
          text-align: left;
        }

        .hub-actions {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .hub-tile-inline {
          background: linear-gradient(135deg, #2A2B37 0%, #3A3B47 100%);
          border: 1px solid #4A4B57;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          color: #f43f5e;
          padding: 8px 12px;
          white-space: nowrap;
          min-width: fit-content;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .hub-tile-inline:hover {
          background: linear-gradient(135deg, #3A3B47 0%, #4A4B57 100%);
          color: #fff;
          border-color: #f43f5e;
          box-shadow: 0 4px 12px rgba(244, 63, 94, 0.3);
          transform: translateY(-1px);
        }

        .hub-tile-inline:hover :global(.hub-icon) {
          transform: scale(1.2) translateY(-2px);
        }

        .hub-grid {
          display: grid;
          grid-template-columns: repeat(2, 65px);
          grid-template-rows: repeat(2, 65px);
          gap: 4px;
          padding: 4px;
          border-radius: 14px;
          border: 1px solid #3A3B47;
          box-shadow: 0 6px 24px rgba(0,0,0,0.4);
        }

        .hub-tile {
          background: #1a1b23;
          border: 1px solid transparent;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          color: #94a3b8;
          padding: 0;
        }

        .hub-tile:hover {
          background: #3A3B47;
          color: #f43f5e;
          border-color: rgba(244, 63, 94, 0.3);
        }

        .hub-tile:hover :global(.hub-icon) {
          transform: scale(1.2) translateY(-2px);
        }

        :global(.hub-icon) {
          font-size: 20px;
          transition: transform 0.3s;
        }

        .hub-label {
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .hub-tag {
          font-size: 10px;
          color: #f43f5e;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
          padding-right: 4px;
        }

        @media (max-width: 768px) {
          .app-header-left {
            flex-direction: row;
            align-items: flex-start;
            gap: 15px;
          }
          .app-image-wrapper {
            flex-shrink: 0;
          }
          .app-info {
            flex: 1;
            min-width: 0;
          }
          .top-left-actions {
            position: absolute;
            top: 20px;
            left: 20px;
            z-index: 10;
          }
          .top-right-actions {
            position: absolute;
            top: 20px;
            right: 20px;
            margin-bottom: 0;
            text-align: right;
            max-width: 60%;
          }
          .app-actions {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
            gap: 15px;
            margin-top: 0;
          }
          .app-actions-left {
            order: 2;
            flex-direction: column;
            gap: 8px;
          }
          .app-actions-right {
            order: 1;
            align-items: flex-start;
            width: 100%;
          }
          .hub-title {
            text-align: left;
            margin-bottom: 6px;
            font-size: 0.8rem;
          }
          .hub-title-inline {
            display: none; /* Hide inline title on mobile */
          }
          .hub-title-mobile {
            display: block; /* Show mobile title on mobile */
          }
          .hub-actions-inline {
            justify-content: flex-start;
            flex-wrap: wrap;
          }
          .hub-tile-inline {
            padding: 4px 8px;
            font-size: 0.75rem;
          }
          .hub-label {
            font-size: 9px;
          }
          .app-name {
            font-size: 1.3rem;
          }
          .app-details {
            font-size: 0.85rem;
          }
          .visit-store-btn {
            padding: 5px 10px;
            font-size: 0.85rem;
          }
          .go-to-dashboard-btn {
            padding: 5px 10px !important;
            font-size: 0.85rem !important;
          }
          .go-back-btn-top {
            padding: 4px 8px !important;
            font-size: 0.8rem !important;
          }
        }

        @media (max-width: 480px) {
          .app-header-left {
            flex-direction: column;
            gap: 12px;
          }
          .app-info {
            width: 100%;
          }
          .top-left-actions {
            position: absolute;
            top: 15px;
            left: 15px;
            z-index: 10;
          }
          .top-right-actions {
            position: absolute;
            top: 15px;
            right: 15px;
            margin-bottom: 0;
            text-align: right;
            max-width: 50%;
          }
          .app-actions {
            flex-direction: column;
            gap: 12px;
          }
          .app-actions-left {
            order: 1;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 6px;
          }
          .app-actions-right {
            order: 2;
            width: 100%;
          }
          .hub-title {
            font-size: 0.75rem;
            margin-bottom: 4px;
          }
          .hub-actions-inline {
            gap: 3px;
          }
          .hub-tile-inline {
            padding: 3px 6px;
            min-width: calc(50% - 2px);
            justify-content: center;
            font-size: 0.7rem;
          }
          .hub-tile-inline .hub-label {
            font-size: 8px;
          }
          :global(.hub-icon) {
            font-size: 14px;
          }
          .app-name {
            font-size: 1.1rem;
          }
          .app-details {
            font-size: 0.8rem;
          }
          .visit-store-btn {
            padding: 4px 8px;
            font-size: 0.8rem;
          }
          .go-to-dashboard-btn {
            padding: 4px 8px !important;
            font-size: 0.8rem !important;
          }
          .go-back-btn-top {
            padding: 3px 6px !important;
            font-size: 0.75rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AppHeader;
